<template>
    <section class="user-tokens">
        <div v-if="emptyHistory" class="tx-history-empty-panel">
            {{ $t('address.tokens.no_tokens') }}

            <a href="https://dedust.io/" class="user-nft__btn" target="_blank">
                {{ $t('address.tokens.buy_tokens') }}
            </a>
        </div>

        <div v-else class="tx-history-wrap">
            <table v-bind:class="isMobile
                ? 'ui-table ui-table-mobile'
                : 'ui-table'">
                <thead v-if="!isMobile">
                    <tr>
                        <th>{{ $t('address.tokens.table_token_name') }}</th>
                        <th width="360" style="max-width: 360px;">{{ $t('address.tokens.table_token_balance') }}</th>
                        <th class="ui-table__cell--align-right">{{ $t('address.tokens.table_token_jetton_wallet') }}</th>
                    </tr>
                </thead>
                <tbody v-if="isLoading">
                    <component
                        v-for="i in 10"
                        v-bind:is="isMobile
                            ? 'tab-token-skeleton-mobile'
                            : 'tab-token-skeleton'"
                        v-bind:key="`tx_skeleton_${i}`"
                    />
                </tbody>
                <tbody v-else>
                    <component
                        v-for="jetton in visibleJettons"
                        v-bind:key="jetton.jetton.address"
                        v-bind:is="isMobile ? 'tab-token-item-mobile' : 'tab-token-item'"
                        v-bind:image="jetton?.jetton.image"
                        v-bind:name="jetton.jetton.name || $t('address.contract_type.unknown')"
                        v-bind:symbol="jetton.jetton.symbol || shortenAddress(jetton.jetton.address)"
                        v-bind:balance="jetton.balance"
                        v-bind:decimals="jetton.jetton.decimals"
                        v-bind:jetton="jetton.jetton.address"
                        v-bind:jetton-wallet="jetton.wallet_address.address"
                        v-bind:jetton-price="getFiatPrice(jetton)"
                        v-bind:balance-price="getFiatPrice(jetton) * jetton.balance / (10 ** jetton.jetton.decimals)"
                        v-bind:currency-symbol="getFiatSymbol(jetton)"
                    />

                    <tr v-if="hiddenJettonsCount > 0" class="jetton-hidden" v-on:click="lowValueVisible = true">
                        <td class="jetton-hidden__cell" v-bind:colspan="isMobile ? 1 : 3">
                            <div class="jetton-hidden-line">
                                <span class="jetton-hidden-line__label">
                                    {{ $tc('address.tokens.hidden_tokens', hiddenJettonsCount) }}
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 15" fill="none"><path stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" d="m1.5 4.75 5.5 5.5 5.5-5.5"/></svg>
                                </span>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <ui-mugen-scroll
            v-bind:handler="loadMore"
            v-bind:shouldHandle="shouldHandleScroll"
            v-bind:showButton="showPreloader"
            v-bind:isLoading="isLoading || isLoadingMore"/>
    </section>
</template>

<script>
import { getJettonsByOwnerTonapi } from '~/api/tonapi';
import TabTokenSkeleton from './TabTokenSkeleton.vue';
import TabTokenSkeletonMobile from './TabTokenSkeletonMobile.vue';
import TabTokenItem from './TabTokenItem.vue';
import TabTokenItemMobile from './TabTokenItemMobile.vue';

/**
 * Берём с запасом: TonAPI отдаёт джеттоны неотсортированными, и на замусоренных
 * кошельках ценные токены уезжают в самый хвост списка. Скрытые строки не рендерятся,
 * так что вся цена такого лимита — вес одного JSON-ответа.
 */
const limit = 100;

/**
 * Токены, чей баланс стоит меньше этой суммы, прячутся под раскрывающуюся строку.
 * При изменении не забыть про тексты hidden_tokens в i18n — сумма в них зашита словами.
 */
const lowValueThresholdUsd = 0.02;

/**
 * Сколько строк оставить, если вся загруженная страница оказалась дешёвым спамом:
 * вываливать сотню мусорных токенов с аватарками смысла нет.
 */
const spamFallbackCount = 10;

export default {
    props: {
        address: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            jettons: [],
            lowValueVisible: false,
            isLoading: true,
            isLoadingMore: false,
            hasMore: true,
        };
    },
    created() {
        this.loadData();
    },
    computed: {
        emptyHistory() {
            return this.isLoading === false && this.jettons.length === 0;
        },
        shouldHandleScroll() {
            return !this.isLoading
                && !this.isLoadingMore
                && this.address
                && this.hasMore
                && this.jettons.length > 0;
        },

        showPreloader() {
            return this.address && this.jettons.length > 0 && this.hasMore;
        },

        /**
         * У эндпоинта нет параметра сортировки, а порядок в ответе — по времени
         * создания jetton-кошельков, поэтому раскладываем по стоимости сами.
         */
        sortedJettons() {
            return this.jettons
                .slice()
                .sort((a, b) => this.getBalancePrice(b) - this.getBalancePrice(a));
        },

        /**
         * Фильтруем только для отрисовки: this.jettons остаётся полным,
         * иначе поедет offset у подгрузки по скроллу.
         */
        visibleJettons() {
            if (this.lowValueVisible) {
                return this.sortedJettons;
            }

            const visible = this.sortedJettons.filter(jetton => !this.isLowValueJetton(jetton));

            if (visible.length > 0) {
                return visible;
            }

            // вся страница дешевле порога — оставляем хотя бы то, что стоит показать
            return this.sortedJettons
                .filter(jetton => this.isWorthShowing(jetton))
                .slice(0, spamFallbackCount);
        },

        hiddenJettonsCount() {
            return this.jettons.length - this.visibleJettons.length;
        },
    },
    methods: {
        /**
         * Цена в валюте из настроек. Если TonAPI её не вернул — остаёмся
         * на долларах, getFiatSymbol тогда тоже покажет $.
         */
        getFiatPrice(jetton) {
            const prices = jetton?.price?.prices || {};
            return prices[this.$store.state.exchangeRateCurrency.toUpperCase()] ?? prices.USD;
        },

        getFiatSymbol(jetton) {
            const currency = this.$store.state.exchangeRateCurrency.toUpperCase();
            const symbols = { USD: '$', RUB: '₽' };
            return jetton?.price?.prices?.[currency] === undefined ? '$' : (symbols[currency] || '$');
        },

        // Сортировка и порог спама считаются в долларах при любой валюте:
        getBalancePrice(jetton) {
            const price = jetton?.price?.prices?.USD || 0;
            const decimals = Number(jetton?.jetton?.decimals) || 0;

            return (price * Number(jetton?.balance || 0)) / (10 ** decimals);
        },

        /**
         * Скам и пустые кошельки не стоит показывать даже в фолбэке: ни то,
         * ни другое не говорит о содержимом кошелька ничего полезного.
         */
        isWorthShowing(jetton) {
            return jetton?.jetton?.verification !== 'blacklist'
                && Number(jetton?.balance || 0) > 0;
        },

        isLowValueJetton(jetton) {
            // у скама цена бывает нарисованной, поэтому чёрный список важнее суммы
            if (jetton?.jetton?.verification === 'blacklist') {
                return true;
            }

            return this.getBalancePrice(jetton) < lowValueThresholdUsd;
        },

        async loadData() {
            try {
                const jettonsList = await getJettonsByOwnerTonapi(this.address, limit);
                this.jettons = jettonsList.balances;

                this.hasMore = jettonsList.balances.length >= limit;
            } catch (error) {
                console.error('Error loading jettons:', error);
            } finally {
                this.isLoading = false;
            }
        },
        async loadMore() {
            // Отдельный флаг: isLoading подменяет весь список скелетонами,
            // а дозагрузка должна оставить уже показанные строки на месте.
            this.isLoadingMore = true;

            try {
                const jettonsList = await getJettonsByOwnerTonapi(this.address, limit, this.jettons.length);

                this.jettons = this.jettons.concat(jettonsList.balances);

                this.hasMore = jettonsList.balances.length >= limit;
            } catch (error) {
                console.error('Error loading more jettons:', error);
            } finally {
                this.isLoadingMore = false;
            }
        },
        shortenAddress(address) {
            if (!address) return null;
            return address.slice(0, 4) + '...' + address.slice(44);
        },
    },

    components: {
        TabTokenSkeleton,
        TabTokenSkeletonMobile,
        TabTokenItem,
        TabTokenItemMobile,
    },
};
</script>

<style lang="scss">
// Сумма и так приглушена .muted, второе приглушение делает «₽» почти невидимым:
.user-tokens .ui-format-price__symbol {
    opacity: 1;
}

.jetton-list__icon {
    width: 24px;
    height: 24px;
    margin-right: 10px;
    &--rastopyrka {
        visibility: hidden;
        height: 0;
    }
}

.jetton-list-link {
    display: flex;
    align-items: center;
}

.tx-token-cell {
    display: flex;
    align-items: center;
    max-width: 100%;
    box-sizing: border-box;
    width: 100%;
}

.ui-table-mobile {
    table-layout: fixed;
}

.ui-table-mobile td {
    padding: 0!important;
    border-top: 0!important;
}

.ui-table-mobile .tx-mobile-content {
    border-bottom: 1px solid var(--card-row-separator);
    padding: 1em 14px 3px 5px;
}

.ui-table-mobile tr:last-child .tx-mobile-content {
    border-bottom: none;
}

.jetton-hidden {
    cursor: pointer;
    user-select: none;
    &__cell {
        padding: 0 !important;
        max-width: none !important;
        white-space: normal !important;
        border-top: 0 !important;
    }
}

.jetton-hidden-line {
    position: relative;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 16px 18px;
    font-size: 13px;
    color: var(--body-muted-text-color);
    &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 18px;
        right: 18px;
        height: 1px;
        background: var(--card-row-separator);
    }
    &__label {
        position: relative;
        display: inline-flex;
        align-items: center;
        padding: 0 12px;
        background: var(--card-background);
        transition: color .15s ease;
        svg {
            width: 12px;
            height: 12px;
            margin-left: 6px;
        }
    }
}

// на мобиле строки токенов уже разделены линиями, ещё одна поперёк — перебор
.ui-table-mobile .jetton-hidden-line {
    padding: 14px;
    &::before {
        display: none;
    }
}

.jetton-hidden:hover .jetton-hidden-line__label {
    color: var(--blue-bright);
}
</style>
